import React from 'react'
import TroubleInfoType from 'components/elements/items/TroubleInfoType'
import styled from 'styled-components'
import { ConfirmImg } from '../ConfirmImg'
import EngineTroubleIcon from 'components/elements/icons/EngineTroubleIcon'
import OthersIcon from 'components/elements/icons/OthersIcon'
import FallingWheelIcon from 'components/elements/icons/FallingWheelIcon'
import AccidentIcon from 'components/elements/icons/AccidentIcon'
import FlatTireIcon from 'components/elements/icons/FlatTireIcon'
import {
  AccidentImage,
  AccidentPartInfo,
  EngineTroubleStatusTypes,
  FieldTypes,
  FuelTypes,
  LossKeyStatusTypes,
  OthersStatusTypes,
  OthersKsStatusTypes,
  TroubleDatetimeTypes,
  TroubleDetailDateTypes,
  TroubleSupportTypes,
  VehicleStateInfo,
} from 'states/QnState'
import {
  VehicleStateFormItem,
  VehicleStateSelectItem,
  VEHICLE_HEIGHT_MODIFICATION_CONFIRM,
  VEHICLE_STATE,
  VEHICLE_STATE_FORM,
} from 'constants/qnState'
import FourWheeledVehicleLine from '../DamagedPartLine/FourWheeledVehicleLine'
import MotorbikeLine from '../DamagedPartLine/MoterbikeLine'
import { isSjDomain, isKsDomain } from 'utils/domain'
import { ConfirmItem, createSelectItem, createSingleItem } from 'utils/qnState'
import EmptyFuelIcon from 'components/elements/icons/EmptyFuelIcon'
import LossKeyIcon from 'components/elements/icons/LossKeyIcon'
import dayjs from 'dayjs'

// 写真の選択情報を生成
function createImageItem<T extends string>(
  formState: VehicleStateSelectItem<T>,
  value: AccidentImage
): ConfirmItem {
  return {
    label: formState.formName,
    values: Object.entries(value)
      .filter(([, v]) => v.image)
      .map(([k, v], index) => (
        <ConfirmImg key={index} src={v.image} alt={formState.options[k as T]} />
      )),
  }
}

// 画像が設定されていたら選択情報を生成する
function createImageItemIfNeeded<T extends string>(
  formState: VehicleStateSelectItem<T>,
  value: AccidentImage
): ConfirmItem | undefined {
  if (Object.values(value).some((obj) => obj.image)) {
    // 画像が指定されていれば生成
    return createImageItem(formState, value)
  }
  return undefined
}

// 車体の場所の選択情報を生成
function createCarPartItem(
  formState: VehicleStateFormItem,
  value: AccidentPartInfo,
  isBike: boolean
): ConfirmItem {
  return {
    label: formState.formName,
    values: [
      value.existsDamagedTire &&
        `【！】${VEHICLE_STATE_FORM.accidentPart.label}`,
      isBike ? (
        <MotorbikeLine selectedParts={value.parts} />
      ) : (
        <FourWheeledVehicleLine selectedParts={value.parts} />
      ),
    ],
  }
}

// 車高改造の情報を生成
function createVehicleHeightItem(info: VehicleStateInfo): ConfirmItem {
  return {
    label: VEHICLE_STATE_FORM.vehicleHeightModification.formName,
    values: [
      info.isVehicleHeightModification
        ? VEHICLE_HEIGHT_MODIFICATION_CONFIRM.YES
        : VEHICLE_HEIGHT_MODIFICATION_CONFIRM.NO,
      info.isVehicleHeightModification
        ? VEHICLE_STATE_FORM.vehicleHeightStatus.options[
            info.vehicleHeightStatus
          ]
        : undefined,
    ],
  }
}

/**
 * お車の状況確認用コンテンツ
 */
const TroubleConfirmContent: React.FC<TroubleConfirmContentProps> = (props) => {
  const {
    troubleDatetime,
    troubleDateDetailTime,
    vehicleState,
    vehicleStateInfo,
  } = props

  // お車の状況
  const createItem = (): [
    React.ReactNode,
    string,
    Array<ConfirmItem | undefined>
  ] => {
    const troubleInfoItems: Array<ConfirmItem | undefined> = []

    // 発生時間
    const DetailTime = dayjs(troubleDateDetailTime.date).format(
      'YYYY年M月D日H時'
    )
    VEHICLE_STATE_FORM.troubleDatetime.options.earlier =
      '上記以前/' + DetailTime
    troubleInfoItems.push(
      createSelectItem(VEHICLE_STATE_FORM.troubleDatetime, troubleDatetime)
    )

    switch (vehicleState) {
      // バッテリー・エンジン関連
      case VEHICLE_STATE.ENGINE_TROUBLE: {
        // トラブル状況
        troubleInfoItems.push(
          createSelectItem(
            VEHICLE_STATE_FORM.engineTroubleStatus,
            vehicleStateInfo.engineTroubleStatus
          )
        )
        // 希望する対応
        if (
          vehicleStateInfo.engineTroubleStatus ===
          EngineTroubleStatusTypes.engineNotStart
        ) {
          troubleInfoItems.push(
            createSelectItem(
              VEHICLE_STATE_FORM.troubleSupport,
              vehicleStateInfo.troubleSupport
            )
          )
        }
        // 車高改造の有無
        if (
          vehicleStateInfo.engineTroubleStatus ===
            EngineTroubleStatusTypes.engineStop ||
          vehicleStateInfo.engineTroubleStatus ===
            EngineTroubleStatusTypes.overheat ||
          (vehicleStateInfo.engineTroubleStatus ===
            EngineTroubleStatusTypes.engineNotStart &&
            vehicleStateInfo.troubleSupport === TroubleSupportTypes.wrecker)
        ) {
          troubleInfoItems.push(
            createSingleItem(
              VEHICLE_STATE_FORM.vehicleHeightModification,
              vehicleStateInfo.isVehicleHeightModification
            )
          )
        }

        return [
          <EngineTroubleIcon key={VEHICLE_STATE.ENGINE_TROUBLE} />,
          VEHICLE_STATE.ENGINE_TROUBLE,
          troubleInfoItems,
        ]
      }
      // パンク
      case VEHICLE_STATE.FLAT_TIRE: {
        // パンク箇所
        troubleInfoItems.push(
          createCarPartItem(
            VEHICLE_STATE_FORM.flatTirePart,
            vehicleStateInfo.accidentPart,
            vehicleStateInfo.isMotorbike
          )
        )
        // タイヤ軸が複数ある車両の選択
        if (isSjDomain() || isKsDomain()) {
          troubleInfoItems.push(
            createSingleItem(
              VEHICLE_STATE_FORM.notApplicableParts,
              vehicleStateInfo.isNotApplicableParts
            )
          )
        }
        // スペアタイヤの有無
        troubleInfoItems.push(
          createSelectItem(
            isKsDomain()
              ? VEHICLE_STATE_FORM.spareTireKsStatus
              : VEHICLE_STATE_FORM.spareTireStatus,
            vehicleStateInfo.spareTireStatus
          )
        )
        // パンク原因
        troubleInfoItems.push(
          createSelectItem(
            isKsDomain()
              ? VEHICLE_STATE_FORM.flatTireKsCause
              : VEHICLE_STATE_FORM.flatTireCause,
            vehicleStateInfo.flatTireCause
          )
        )
        // 車高改造の有無
        troubleInfoItems.push(createVehicleHeightItem(vehicleStateInfo))
        return [
          <FlatTireIcon key={VEHICLE_STATE.FLAT_TIRE} />,
          VEHICLE_STATE.FLAT_TIRE,
          troubleInfoItems,
        ]
      }
      // 事故
      case VEHICLE_STATE.ACCIDENT: {
        if (vehicleStateInfo.noPhoto) {
          // 写真以外
          // 写真補足があれば追加
          troubleInfoItems.push(
            createImageItemIfNeeded(
              VEHICLE_STATE_FORM.accidentImage,
              vehicleStateInfo.accidentImage
            )
          )
          // トラブル状況
          troubleInfoItems.push(
            createSelectItem(
              VEHICLE_STATE_FORM.accidentStatus,
              vehicleStateInfo.accidentStatus
            )
          )
          // 車高改造
          troubleInfoItems.push(createVehicleHeightItem(vehicleStateInfo))
          // 車体の場所選択
          troubleInfoItems.push(
            createCarPartItem(
              VEHICLE_STATE_FORM.accidentPart,
              vehicleStateInfo.accidentPart,
              vehicleStateInfo.isMotorbike
            )
          )
        } else {
          // 写真選択
          troubleInfoItems.push(
            createImageItem(
              VEHICLE_STATE_FORM.accidentImage,
              vehicleStateInfo.accidentImage
            )
          )
          // トラブル状況
          troubleInfoItems.push(
            createSelectItem(
              VEHICLE_STATE_FORM.accidentStatus,
              vehicleStateInfo.accidentStatus
            )
          )
          // 車高改造の有無
          troubleInfoItems.push(createVehicleHeightItem(vehicleStateInfo))
        }
        return [
          <AccidentIcon key={VEHICLE_STATE.ACCIDENT} />,
          VEHICLE_STATE.ACCIDENT,
          troubleInfoItems,
        ]
      }
      // 落輪・乗り上げ
      case VEHICLE_STATE.FALLING_WHEEL: {
        if (vehicleStateInfo.noPhoto) {
          // 写真以外
          // 写真補足があれば追加
          troubleInfoItems.push(
            createImageItemIfNeeded(
              VEHICLE_STATE_FORM.accidentImage,
              vehicleStateInfo.accidentImage
            )
          )
          // 車体の場所選択
          troubleInfoItems.push(
            createCarPartItem(
              VEHICLE_STATE_FORM.accidentPart,
              vehicleStateInfo.accidentPart,
              vehicleStateInfo.isMotorbike
            )
          )
        } else {
          // 写真
          troubleInfoItems.push(
            createImageItem(
              VEHICLE_STATE_FORM.accidentImage,
              vehicleStateInfo.accidentImage
            )
          )
        }
        // トラブル状況
        troubleInfoItems.push(
          createSelectItem(
            VEHICLE_STATE_FORM.fallingWheelStatus,
            vehicleStateInfo.fallingWheelStatus
          )
        )
        // 現場の種類
        const fieldTypeItem = createSelectItem(
          VEHICLE_STATE_FORM.fieldType,
          vehicleStateInfo.fieldType.type
        )
        if (vehicleStateInfo.fieldType.type === FieldTypes.others) {
          // valuesにテキストを追加
          troubleInfoItems.push({
            label: fieldTypeItem.label,
            values: fieldTypeItem.values.concat(
              vehicleStateInfo.fieldType.others
            ),
          })
        } else {
          troubleInfoItems.push(fieldTypeItem)
        }
        // 車高改造の有無
        troubleInfoItems.push(createVehicleHeightItem(vehicleStateInfo))
        return [
          <FallingWheelIcon key={VEHICLE_STATE.FALLING_WHEEL} />,
          VEHICLE_STATE.FALLING_WHEEL,
          troubleInfoItems,
        ]
      }
      // 燃料切れ
      case VEHICLE_STATE.EMPTY_FUEL: {
        // トラブル状況
        troubleInfoItems.push(
          createSelectItem(
            VEHICLE_STATE_FORM.fuelType,
            vehicleStateInfo.fuelType
          )
        )
        if (vehicleStateInfo.fuelType === FuelTypes.electricity) {
          // 電気・LGP・水素(その他特殊燃料)
          // 車高改造の有無
          troubleInfoItems.push(createVehicleHeightItem(vehicleStateInfo))
        }
        return [
          <EmptyFuelIcon key={VEHICLE_STATE.EMPTY_FUEL} />,
          VEHICLE_STATE.EMPTY_FUEL,
          troubleInfoItems,
        ]
      }
      // 鍵のトラブル
      case VEHICLE_STATE.LOSS_KEY: {
        // トラブル状況
        troubleInfoItems.push(
          createSelectItem(
            VEHICLE_STATE_FORM.lossKeyStatus,
            vehicleStateInfo.lossKeyStatus
          )
        )
        if (vehicleStateInfo.lossKeyStatus === LossKeyStatusTypes.insideCar) {
          // カギの閉じ込み
          // 車内の状況
          if (vehicleStateInfo.insideVehicleStatus.length > 0) {
            troubleInfoItems.push(
              createSelectItem(
                VEHICLE_STATE_FORM.insideVehicleStatus,
                vehicleStateInfo.insideVehicleStatus
              )
            )
          }
        } else if (vehicleStateInfo.lossKeyStatus === LossKeyStatusTypes.loss) {
          // カギの紛失
          // 車高改造の有無
          troubleInfoItems.push(createVehicleHeightItem(vehicleStateInfo))
        }
        return [
          <LossKeyIcon key={VEHICLE_STATE.LOSS_KEY} />,
          VEHICLE_STATE.LOSS_KEY,
          troubleInfoItems,
        ]
      }
      // その他
      case VEHICLE_STATE.OTHERS:
      default: {
        // トラブル状況
        troubleInfoItems.push(
          isKsDomain()
            ? createSelectItem(
                VEHICLE_STATE_FORM.othersKsStatus,
                vehicleStateInfo.othersKsStatus
              )
            : createSelectItem(
                VEHICLE_STATE_FORM.othersStatus,
                vehicleStateInfo.othersStatus
              )
        )
        // 詳細
        troubleInfoItems.push(
          vehicleStateInfo.othersDetail
            ? createSingleItem(
                VEHICLE_STATE_FORM.othersDetail,
                vehicleStateInfo.othersDetail
              )
            : undefined
        )
        // 画像
        if (
          (!isKsDomain() &&
            (vehicleStateInfo.othersStatus === OthersStatusTypes.snow ||
              vehicleStateInfo.othersStatus === OthersStatusTypes.others)) ||
          // 共済系ドメイン時は雪、泥・砂もしくはその他が選択時に画像をPushする
          (isKsDomain() &&
            (vehicleStateInfo.othersKsStatus === OthersKsStatusTypes.snow ||
              vehicleStateInfo.othersKsStatus === OthersKsStatusTypes.sand ||
              vehicleStateInfo.othersKsStatus === OthersKsStatusTypes.others))
        ) {
          troubleInfoItems.push(
            createImageItemIfNeeded(
              VEHICLE_STATE_FORM.othersImage,
              vehicleStateInfo.othersImage
            )
          )
        }
        // 車高改造の有無
        troubleInfoItems.push(
          createSingleItem(
            VEHICLE_STATE_FORM.vehicleHeightModification,
            vehicleStateInfo.isVehicleHeightModification
          )
        )
        return [
          <OthersIcon key={VEHICLE_STATE.OTHERS} />,
          VEHICLE_STATE.OTHERS,
          troubleInfoItems,
        ]
      }
    }
  }
  const [icon, label, items] = createItem()

  return (
    <React.Fragment>
      <TroubleInfoType icon={icon} label={label} />
      {items.map(
        (item, index) =>
          item && (
            <TroubleInfoItemArea key={index}>
              <TroubleInfoItemLabel>{item.label}</TroubleInfoItemLabel>
              {item.values.map((item, index) =>
                typeof item === 'string' ? (
                  <TroubleInfoItemValue key={index}>
                    <TroubleInfoItemValueChild>
                      {item}
                    </TroubleInfoItemValueChild>
                  </TroubleInfoItemValue>
                ) : (
                  <TroubleInfoItemComponentArea key={index}>
                    {item}
                  </TroubleInfoItemComponentArea>
                )
              )}
            </TroubleInfoItemArea>
          )
      )}
    </React.Fragment>
  )
}

export type TroubleConfirmContentProps = {
  /** 発生日時 */
  troubleDatetime: TroubleDatetimeTypes
  /** 発生日時詳細 */
  troubleDateDetailTime: TroubleDetailDateTypes
  /** トラブルの種類 */
  vehicleState: string
  /** トラブル状況 */
  vehicleStateInfo: VehicleStateInfo
}

const TroubleInfoItemArea = styled.div`
  margin-top: 24px;
`

const TroubleInfoItemLabel = styled.div`
  font-weight: bold;
`

const TroubleInfoItemComponentArea = styled.div`
  margin-top: 16px;
`

const TroubleInfoItemValue = styled.ul`
  margin-top: 8px;
  padding-left: 34px;
`

const TroubleInfoItemValueChild = styled.li`
  word-wrap: break-word;
`

export default React.memo<TroubleConfirmContentProps>(TroubleConfirmContent)
